import { type ReactElement } from 'react';

import { Menu } from '../../../navigation/menu/Menu.js';
import type { PropsFromGrid } from '../../core/components/single-value-renderer/types/single-value-base-props.js';
import { toolbarMenuGroup } from '../../core/components/toolbar/components/ToolbarMenu.sty.js';
import { renderMenuActionChildren } from '../../core/utils/render-menu-action-children.js';
import type {
  PartialSingleValueGridConfig,
  SingleValueGridIntentsConfig,
} from '../types/single-value-grid.js';

type SingleIntentsData = Record<string, unknown>;

const getIntentsAsyncTemplate =
  (
    singleIntents: SingleValueGridIntentsConfig['single'],
    gridIntents: SingleValueGridIntentsConfig['grid'],
    data: SingleIntentsData,
    labelText: string,
  ) =>
  async (): Promise<ReactElement> => {
    const intentsTemplate = gridIntents.length
      ? [
          <Menu.Group key="group" className={toolbarMenuGroup}>
            {gridIntents.map((item, key) => (
              <Menu.Intent {...item} key={key} />
            ))}
          </Menu.Group>,
        ]
      : [];

    if (singleIntents) {
      return (async () => singleIntents(data))()
        .then((template) => {
          return (
            <>
              {template
                ? [
                    <Menu.Group
                      key="individualIntents"
                      className={toolbarMenuGroup}
                    >
                      {renderMenuActionChildren(template.props.children, {
                        divideActionsAndIntents: false,
                      })}
                    </Menu.Group>,
                    gridIntents.length ? (
                      <Menu.Label key="label">{labelText}</Menu.Label>
                    ) : null,
                    ...intentsTemplate,
                  ]
                : intentsTemplate}
            </>
          );
        })
        .catch(() => <>{intentsTemplate}</>);
    }

    return <>{intentsTemplate}</>;
  };

export const buildPropsFromGrid =
  (
    toolbar: PartialSingleValueGridConfig['toolbar'],
    singleIntents: SingleValueGridIntentsConfig['single'],
    gridIntents: SingleValueGridIntentsConfig['grid'],
    labelText: string,
  ) =>
  (data: SingleIntentsData) => {
    if (!singleIntents) {
      return undefined;
    }

    return {
      intentsAsyncTemplate: getIntentsAsyncTemplate(
        singleIntents,
        gridIntents,
        data,
        labelText,
      ),
      toolbar: {
        ...toolbar,
        draggable: false,
      } as PropsFromGrid['toolbar'],
    };
  };
